import { useT } from "@transifex/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Redirect, useLocation, useParams } from "react-router-dom";
import LoadingSpinner from "../../basic/loadingSpinner/LoadingSpinner";
import LegalFooter from "../../complex/legalFooter/LegalFooter";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { useClassMember } from "../../context/ClassMemberContext";
import { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { isPrivateSchoolClass } from "../../utils/schoolClassUtils";
import {
    CLASSROOM_PREVIEW_PARAM,
    getClassroomLoginRouteWithReturnTo,
    getClassroomRootRoute,
    isClassroomPreviewSearch,
} from "../../helpers/schoolClassHelpers";
import { NetworkErrorCodes } from "../../networking/errorCodes";
import {
    getClassByToken,
    getClassTeacherInfo,
    type PublicSchoolClass,
    type TeacherInfo,
} from "../../networking/schoolClasses";
import ContentWithHeader from "../../wrapper/ContentWithHeader/ContentWithHeader";
import ClassroomDashboard from "../classroomDashboard/ClassroomDashboard";

interface RouteParams {
    schoolClassId: string;
}

enum FetchStatuses {
    LOADING,
    FOUND,
    NOT_FOUND,
    ERROR,
}

const ClassroomShell = () => {
    const t = useT();
    const params = useParams<RouteParams>();
    const location = useLocation();
    const urlSchoolClassId = (params.schoolClassId ?? "").toUpperCase();
    const { member, discardStaleSession, isRestoringSession } = useClassMember();
    const { userInfo, getAndSetUserAndTeacherInfo } = useGeneralContext();

    const [fetchStatus, setFetchStatus] = useState(FetchStatuses.LOADING);
    const [classInfo, setClassInfo] = useState<PublicSchoolClass | null>(null);
    const [teacher, setTeacher] = useState<TeacherInfo | null>(null);

    const authTriggeredRef = useRef(false);

    const hasPreviewParam = isClassroomPreviewSearch(location.search);

    // Preview requires a logged-in teacher: a student who follows a leaked ?preview=1 link must get
    // their normal classroom, not the locked-down preview. /klassenzimmer has no auth-bootstrapping
    // route, so resolve the teacher session here — only when the param is present, sparing every
    // ordinary classroom visit the extra auth round-trip.
    useEffect(() => {
        if (!hasPreviewParam || authTriggeredRef.current) return;
        if (userInfo?.visitorOrUserRole || userInfo?.isAnonymousUser) return;
        authTriggeredRef.current = true;
        getAndSetUserAndTeacherInfo();
    }, [hasPreviewParam, userInfo, getAndSetUserAndTeacherInfo]);

    const t_classLoadError = t("We couldn't load this class. Please check your connection and try again.", {
        _tags: "ClassMemberLogin, ClassroomShell",
    });

    useEffect(() => {
        let cancelled = false;
        setFetchStatus(FetchStatuses.LOADING);
        setTeacher(null);
        (async () => {
            const [classResult, teacherResult] = await Promise.allSettled([
                getClassByToken(urlSchoolClassId),
                getClassTeacherInfo(urlSchoolClassId),
            ]);
            if (cancelled) return;
            if (classResult.status === "rejected") {
                const isNotFound =
                    axios.isAxiosError(classResult.reason) &&
                    classResult.reason.response?.status === NetworkErrorCodes.NOT_FOUND;
                setFetchStatus(isNotFound ? FetchStatuses.NOT_FOUND : FetchStatuses.ERROR);
                return;
            }
            setClassInfo(classResult.value);
            setTeacher(teacherResult.status === "fulfilled" ? teacherResult.value : null);
            setFetchStatus(FetchStatuses.FOUND);
        })();
        return () => {
            cancelled = true;
        };
    }, [urlSchoolClassId]);

    const isPrivate = isPrivateSchoolClass(classInfo?.classVisibility);
    const memberMatches = member?.schoolClassId.toUpperCase() === urlSchoolClassId;
    // Treat a non-matching session as absent during render: this tab only ever acts on a member of
    // the class in its URL.
    const matchedMember = memberMatches ? member : null;

    // Without the discard, a session whose class no longer resolves loops instead: `/klassenzimmer` sends
    // a logged-in member straight back here. The BE cascade-deletes members with their class (verified
    // 2026-08-11), so the deleted-class case should no longer arise — the guard stays for any other NOT_FOUND.
    // A member of a *different* class on a public classroom is deliberately not discarded: the session is
    // shared across tabs and another tab may be actively using it (HALLOFUSS-6670); the public dashboard
    // never reads the member anyway.
    useEffect(() => {
        const memberSessionOnDeletedClass = fetchStatus === FetchStatuses.NOT_FOUND && memberMatches;
        if (memberSessionOnDeletedClass) {
            discardStaleSession();
        }
    }, [fetchStatus, memberMatches, discardStaleSession]);

    // <Redirect> navigates synchronously, beating the passive session-discard effect above. Both guards
    // prevent a pointless bounce through ClassroomLogin and back: `memberMatches` waits for the discard
    // to clear a stale member, `isRestoringSession` waits out the null `member` read mid-restore.
    if (fetchStatus === FetchStatuses.NOT_FOUND && !memberMatches && !isRestoringSession) {
        return <Redirect to={getClassroomRootRoute()} />;
    }

    const isTeacher = userInfo.visitorOrUserRole?.toLowerCase() === "teacher";
    const isRoleResolved = !!userInfo.visitorOrUserRole || !!userInfo.isAnonymousUser;
    const isPreview = hasPreviewParam && isTeacher;

    // With the preview param present, hold the FOUND render until the role check resolves —
    // rendering with isPreview=false and flipping later would flash an interactive classroom
    // without the preview chrome. renderBody shows the spinner meanwhile.
    const isAwaitingPreviewRole = hasPreviewParam && !isRoleResolved;

    if (fetchStatus === FetchStatuses.FOUND && classInfo && !isAwaitingPreviewRole) {
        if (!isPrivate) {
            return (
                <ClassroomDashboard
                    variant={SchoolClassVisibilities.PUBLIC}
                    schoolClassId={urlSchoolClassId}
                    schoolClassName={classInfo.name}
                    teacher={teacher}
                    isPreview={isPreview}
                />
            );
        }
        if (matchedMember) {
            return (
                <ClassroomDashboard
                    variant={SchoolClassVisibilities.PRIVATE}
                    schoolClassId={urlSchoolClassId}
                    schoolClassName={classInfo.name}
                    teacher={teacher}
                    memberName={matchedMember.name}
                    isPreview={isPreview}
                />
            );
        }
        // Strip the preview flag before round-tripping the URL through the login: a student who
        // follows a leaked ?preview=1 link must land in their normal classroom, not in the
        // locked-down preview. The teacher flow never hits this redirect — its silent-login URL
        // builds its own returnTo (getClassMemberPreviewLoginRoute).
        const loginSearchParams = new URLSearchParams(location.search);
        loginSearchParams.delete(CLASSROOM_PREVIEW_PARAM);
        const remainingSearch = loginSearchParams.toString();
        const returnTo = `${location.pathname}${remainingSearch ? `?${remainingSearch}` : ""}`;
        return <Redirect to={getClassroomLoginRouteWithReturnTo(urlSchoolClassId, returnTo)} />;
    }

    const renderBody = () => {
        switch (fetchStatus) {
            case FetchStatuses.LOADING:
            case FetchStatuses.NOT_FOUND:
                return <LoadingSpinner />;
            case FetchStatuses.ERROR:
                return <p>{t_classLoadError}</p>;
            default:
                // FOUND only reaches here while the preview role check is pending.
                return <LoadingSpinner />;
        }
    };

    return (
        <ContentWithHeader footer={<LegalFooter />}>
            <div className="flex flex-1 flex-col w-full">
                <div className="flex flex-1 items-center justify-center p-s w-full">{renderBody()}</div>
            </div>
        </ContentWithHeader>
    );
};

export default ClassroomShell;
